import {
  ProductDetailsColor,
  ProductDetailsMediaColor,
} from '../../../../services/modules/productDetails/productDetails.types';
import { ESProductPrice, ESProductSearch } from '../../../products/ProductTypes';
import { ESProduct } from '../../../products/product.types.v2';
import { OfflineDatabaseProductColor } from '../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { OfflineDatabaseProductColorPriceInfoV2 } from '../../database/offlineDatabase/types/offlineDatabaseProductColorPriceInfo.types';
import { offlineClassificationsRepository } from '../../repositories/offlineClassifications.repository';
import { offlineProductColorPriceInfoRepository } from '../../repositories/offlineProductColorPriceInfo.repository';
import { offlineProductColorsRepository } from '../../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../../repositories/offlineProducts.repository';
import OfflinePriceTableUtils from '../../repositories/utils/offlinePriceTable.utils';
import { OfflineProductColorPriceInfoTransformers } from '../OfflineDatabaseProductColorPriceInfo.transformers';
import { OfflineProductColorTransformers } from './OfflineDatabaseProductColor.transformers';

export default class OfflineProductColorToProductDetailsTransformers {
  static async toProductDetailsESProductSearch(
    offlineProductColor: OfflineDatabaseProductColor,
    orderId?: string,
  ): Promise<ESProductSearch> {
    const { product, allProductColors, colorClassifications } =
      await this.getProductColorInfo(offlineProductColor);

    return {
      name: product.name,
      code: product.code,
      resume: product.resume,
      description: product.description,
      referenceCode: product.referenceCode,
      productColorId: offlineProductColor.id,
      productId: offlineProductColor.productId,
      availability: offlineProductColor.availabilities,
      prices: await this.toProductDetailsESProductColorPrices(offlineProductColor, orderId),
      color: OfflineProductColorTransformers.toESProductColor(offlineProductColor),
      medias: OfflineProductColorTransformers.toESProductMedias(offlineProductColor),
      allColors: allProductColors.map(OfflineProductColorTransformers.toESProductColor),
      colorClassifications: colorClassifications.map((classification) => ({
        id: classification.id,
        code: classification.code,
        name: classification.name,
        classificationType: {
          useAsTag: true,
          id: classification.classificationTypeId,
        } as ESProductSearch['colorClassifications'][0]['classificationType'],
      })),
      tags: [],
      grid: [],
      active: true,
      brandId: '',
      catalogs: [],

      promotions: [],
      deliveryWindows: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      category: {} as ESProductSearch['category'],
      resumeInfo: {} as ESProductSearch['resumeInfo'],
      collection: {} as ESProductSearch['collection'],
    };
  }

  private static async getProductColorInfo(offlineProductColor: OfflineDatabaseProductColor) {
    const [product, allProductColors, colorClassifications] = await Promise.all([
      offlineProductsRepository.getByIdOrFail(offlineProductColor.productId),
      offlineProductColorsRepository.findByProductId(offlineProductColor.productId),
      offlineClassificationsRepository.getClassificationsToUseAsTagByIds(
        offlineProductColor.classificationIds,
      ),
    ]);
    return { product, allProductColors, colorClassifications };
  }

  private static async toProductDetailsESProductColorPrices(
    offlineProductColor: OfflineDatabaseProductColor,
    orderId?: string,
  ): Promise<ESProductPrice[]> {
    const priceTableId = await OfflinePriceTableUtils.getOrderOrDefaultPriceTableId(orderId);

    const price = (await offlineProductColorPriceInfoRepository.getByIdOrFail(
      offlineProductColor.id,
      priceTableId,
    )) as OfflineDatabaseProductColorPriceInfoV2;

    return price.pricesToSizes.map<ESProductPrice>((priceToSize) => ({
      sizeId: priceToSize.sizeId,
      priceTableId: price.priceTableId,
      retailPrice: priceToSize.retailPrice,
      wholesalePrice: priceToSize.wholesalePrice,
      promotionalPrice: priceToSize.promotionalPrice,
      skuId: '',
      sizeName: '',
      productSizeId: '',
      priceTableName: '',
      priceTableSkuId: '',
      productSizeName: '',
      productSizePosition: 0,
      productSizeUnique: false,
      priceComposition: undefined,
      promotion: {} as ESProductPrice['promotion'],
    }));
  }

  static toProductDetailsColor(
    offlineProductColor: OfflineDatabaseProductColor,
  ): ProductDetailsColor {
    return {
      code: offlineProductColor.code,
      name: offlineProductColor.name,
      isImage: offlineProductColor.isImage,
      hexcode: offlineProductColor.hexcode,
      imageUrl: offlineProductColor.imageUrl,
      productColorId: offlineProductColor.id,
    };
  }

  static toProductDetailsMediaColor(
    offlineProductColor: OfflineDatabaseProductColor,
  ): ProductDetailsMediaColor {
    return {
      ...OfflineProductColorToProductDetailsTransformers.toProductDetailsColor(offlineProductColor),
      colorId: offlineProductColor.colorId,
      available: true,
      medias: offlineProductColor.medias,
    };
  }

  static async toProductDetailsESProduct(
    productColors: OfflineDatabaseProductColor[],
    orderId?: string,
  ): Promise<ESProduct[]> {
    const esProducts: ESProduct[] = [];

    for (const productColor of productColors) {
      const { product, allProductColors, productColorPriceInfo } = await this.getItemInfo(
        productColor,
        orderId,
      );
      const hasNonZeroPrice = productColorPriceInfo?.pricesToSizes.some(
        (price) => price.wholesalePrice > 0,
      );

      hasNonZeroPrice &&
        esProducts.push({
          tags: [],
          name: product.name,
          code: product.code,
          colorClassifications: [],
          productColorId: productColor.id,
          productId: productColor.productId,
          referenceCode: product.referenceCode,
          availability: productColor.availabilities,
          medias: productColor.medias.map((media) => ({
            url: media.url,
            type: media.type,
            position: media.position,
          })),
          color: {
            code: productColor.code,
            name: productColor.name,
            id: productColor.productId,
            hexcode: productColor.hexcode,
            isImage: productColor.isImage,
            imageUrl: productColor.imageUrl,
            productColorId: productColor.id,
          },
          allColors: allProductColors.map((pc) => ({
            code: pc.code,
            name: pc.name,
            id: pc.id,
            hexcode: pc.hexcode,
            isImage: pc.isImage,
            imageUrl: pc.imageUrl,
            productColorId: pc.id,
          })),
          prices: productColorPriceInfo
            ? await OfflineProductColorPriceInfoTransformers.toESProductPrice(productColorPriceInfo)
            : [],
        });
    }

    return esProducts;
  }

  private static async getItemInfo(productColor: OfflineDatabaseProductColor, orderId?: string) {
    const priceTableId = await OfflinePriceTableUtils.getOrderOrDefaultPriceTableId(orderId);

    const [product, allProductColors, productColorPriceInfo] = await Promise.all([
      offlineProductsRepository.getByIdOrFail(productColor.productId),
      offlineProductColorsRepository.findByProductId(productColor.productId),
      offlineProductColorPriceInfoRepository.getById(productColor.id, priceTableId),
    ]);

    return {
      product,
      allProductColors,
      productColorPriceInfo:
        productColorPriceInfo?.version === 2 ? productColorPriceInfo : undefined,
    };
  }
}
